import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { leadFormApi } from '../lib/api';

const PROJECT_TIMEFRAMES = [
  { value: 'immediately', label: 'Immediately' },
  { value: '0-6-months', label: '0-6 Months' },
  { value: '6-12-months', label: '6-12 Months' },
  { value: 'planning', label: 'Planning Stage' },
];

export default function PublicLeadForm() {
  const { formToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    project_description: '',
    project_timeframe: '',
  });

  useEffect(() => {
    loadFormConfig();
  }, [formToken]);

  // Apply branding colors when formConfig loads
  useEffect(() => {
    if (formConfig?.branding?.color_scheme) {
      const schemes = {
        default: { primary: '#f97316', hsl: '24 95% 53%' },
        blue: { primary: '#2563eb', hsl: '221 83% 53%' },
        green: { primary: '#16a34a', hsl: '142 76% 36%' },
        purple: { primary: '#9333ea', hsl: '271 81% 56%' }
      };
      const colors = schemes[formConfig.branding.color_scheme] || schemes.default;
      document.documentElement.style.setProperty('--brand-primary', colors.primary);
      document.documentElement.style.setProperty('--primary', colors.hsl);
    }
  }, [formConfig]);

  const loadFormConfig = async () => {
    try {
      const response = await leadFormApi.getPublicForm(formToken);
      setFormConfig(response.data);
    } catch (err) {
      setError('This lead form is not available.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate required fields
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email && !formData.phone) {
      setError('Please provide either email or phone number');
      return;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return;
    }
    if (!formData.zip_code.trim()) {
      setError('Zip code is required');
      return;
    }

    setSubmitting(true);

    try {
      await leadFormApi.submitForm(formToken, formData);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e.message).join(', '));
      } else {
        setError('Failed to submit form. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error && !formConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full rounded-sm">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Form Not Available</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const settings = formConfig?.settings || {};
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full rounded-sm">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600">{settings.success_message || "We've received your request and will be in touch soon."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const settings = formConfig?.settings || {};
  const companyName = formConfig?.company_name || '';
  const primaryColor = settings.colors?.primary || '#f97316';
  const customLogo = formConfig?.branding?.custom_logo;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <Card className="max-w-lg mx-auto rounded-sm">
        <CardHeader className="text-center border-b">
          <div className="flex items-center justify-center gap-2 mb-2">
            {customLogo ? (
              <img 
                src={customLogo} 
                alt={companyName || 'Company Logo'} 
                className="h-12 w-auto max-w-[150px] object-contain"
              />
            ) : (
              <div 
                className="h-10 w-10 rounded-sm flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <Building2 className="h-6 w-6 text-white" />
              </div>
            )}
            {!customLogo && companyName && <span className="font-bold text-lg">{companyName}</span>}
          </div>
          {customLogo && companyName && <span className="font-bold text-lg mb-2">{companyName}</span>}
          <CardTitle className="text-2xl">{settings.title || 'Get a Free Quote'}</CardTitle>
          <CardDescription>{settings.subtitle || "Fill out the form below and we'll get back to you within 24 hours."}</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-sm text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="John"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Smith"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">
                  Email <span className="text-muted-foreground text-xs">(or phone)</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">
                  Phone <span className="text-muted-foreground text-xs">(or email)</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">* At least one contact method required</p>

            {/* Company */}
            <div>
              <Label htmlFor="company">
                Company <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="If you're a contractor / business"
                className="rounded-sm mt-1"
              />
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address">
                Street Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main Street"
                className="rounded-sm mt-1"
                required
              />
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-3">
                <Label htmlFor="city">
                  City <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Springfield"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              <div className="col-span-1">
                <Label htmlFor="state">
                  State <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="IL"
                  maxLength={2}
                  className="rounded-sm mt-1"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="zip_code">
                  Zip <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  placeholder="62701"
                  className="rounded-sm mt-1"
                  required
                />
              </div>
            </div>

            {/* Project Timeframe */}
            <div>
              <Label>Project Timeframe</Label>
              <Select 
                value={formData.project_timeframe} 
                onValueChange={(v) => handleSelectChange('project_timeframe', v)}
              >
                <SelectTrigger className="rounded-sm mt-1">
                  <SelectValue placeholder="When do you need this done?" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TIMEFRAMES.map((tf) => (
                    <SelectItem key={tf.value} value={tf.value}>
                      {tf.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Description */}
            <div>
              <Label htmlFor="project_description">Brief Project Description</Label>
              <Textarea
                id="project_description"
                name="project_description"
                value={formData.project_description}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                className="rounded-sm mt-1 min-h-[100px]"
                rows={4}
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-sm h-11 text-white font-semibold"
              style={{ backgroundColor: primaryColor }}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                settings.submit_button_text || 'Request Quote'
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting this form, you agree to be contacted about your project.
            </p>
          </form>
        </CardContent>
      </Card>
      
      {/* Powered by footer */}
      <div className="text-center mt-4 text-xs text-muted-foreground">
        Powered by <a href="https://corbel.work" className="underline hover:text-slate-700" target="_blank" rel="noopener noreferrer">Corbel CRM</a>
      </div>
    </div>
  );
}
